/**
 * OnboardingStep2Page — profile information (/onboarding/2).
 * Receives userType from location state; navigates to /onboarding/3 on completion.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useOnboardingStep2,
  useCurrentUser,
  OnboardingStudentProfileRequest,
  OnboardingParentProfileRequest,
} from '@/lib/api/auth';
import { getErrorMessage } from '@/lib/api/client';
import OnboardingStep2Profile from '@/components/onboarding/OnboardingStep2Profile';

export type UserProfileData =
  | OnboardingStudentProfileRequest
  | OnboardingParentProfileRequest
  | null;

export default function OnboardingStep2Page() {
  const navigate = useNavigate();
  const location = useLocation();

  const userType: 'student' | 'parent' = location.state?.userType ?? 'student';

  const { data: user, isLoading: userLoading } = useCurrentUser();
  const step2Mutation = useOnboardingStep2();

  useEffect(() => {
    if (!userLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, userLoading, navigate]);

  const handleNext = async (data: UserProfileData) => {
    try {
      if (data) {
        await step2Mutation.mutateAsync(data);
      }
      navigate('/onboarding/3', { state: { userType, profileData: data } });
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  };

  if (userLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <OnboardingStep2Profile
      userType={userType}
      onNext={handleNext}
      onBack={() => navigate('/onboarding/1')}
      isLoading={step2Mutation.isPending}
    />
  );
}
